import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { TopBar } from '../components/layout/TopBar';
import { useAuthStore } from '../store/useStore';
import api from '../lib/api';
import { 
  Plus, Trash2, Save, Heart, Gift, Briefcase, GraduationCap, 
  Users2, Sparkles, Home, HelpCircle, Smile, Star, Music, 
  Camera, Coffee, Utensils, Wine, PartyPopper, Cake, Edit2,
  Handshake, Baby, Accessibility, Ban, Sofa, Gem, UserPlus, MessageSquare
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Biblioteca de iconos disponibles
const ICON_LIBRARY = [
  { id: 'Heart', icon: Heart, label: 'Corazón' },
  { id: 'Gift', icon: Gift, label: 'Regalo' },
  { id: 'Briefcase', icon: Briefcase, label: 'Negocios' },
  { id: 'GraduationCap', icon: GraduationCap, label: 'Graduación' },
  { id: 'Users2', icon: Users2, label: 'Grupo' },
  { id: 'Sparkles', icon: Sparkles, label: 'Especial' },
  { id: 'Home', icon: Home, label: 'Familia' },
  { id: 'Smile', icon: Smile, label: 'Feliz' },
  { id: 'Star', icon: Star, label: 'Estrella' },
  { id: 'Music', icon: Music, label: 'Música' },
  { id: 'Camera', icon: Camera, label: 'Cámara' },
  { id: 'Coffee', icon: Coffee, label: 'Café' },
  { id: 'Utensils', icon: Utensils, label: 'Comida' },
  { id: 'Wine', icon: Wine, label: 'Vino' },
  { id: 'PartyPopper', icon: PartyPopper, label: 'Fiesta' },
  { id: 'Cake', icon: Cake, label: 'Cumpleaños' },
  { id: 'Handshake', icon: Handshake, label: 'Reunión' },
  { id: 'Gem', icon: Gem, label: 'Propuesta' },
  { id: 'Ban', icon: Ban, label: 'Ninguna' },
];

// Ocasiones predefinidas del sistema
const DEFAULT_OCCASIONS = [
  { id: 'anniversary', label: 'Aniversario', icon: 'Heart', isDefault: true },
  { id: 'romantic', label: 'Cita romántica', icon: 'Heart', isDefault: true },
  { id: 'birthday', label: 'Cumpleaños', icon: 'Cake', isDefault: true },
  { id: 'friends', label: 'Encuentro entre amigos', icon: 'Users2', isDefault: true },
  { id: 'graduation', label: 'Graduación', icon: 'GraduationCap', isDefault: true },
  { id: 'business', label: 'Negocios', icon: 'Briefcase', isDefault: true },
  { id: 'proposal', label: 'Propuesta', icon: 'Gem', isDefault: true },
  { id: 'family', label: 'Reunión familiar', icon: 'Home', isDefault: true },
  { id: 'none', label: 'No la hay', icon: 'Ban', isDefault: true },
];

export default function WidgetOccasions() {
  const { user, token } = useAuthStore();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [occasions, setOccasions] = useState([]);
  const [customOccasions, setCustomOccasions] = useState([]);
  const [enabledDefaults, setEnabledDefaults] = useState(DEFAULT_OCCASIONS.map(o => o.id));
  const [showIconPicker, setShowIconPicker] = useState(null);
  const [newOccasion, setNewOccasion] = useState({ label: '', icon: 'Star' });
  const [cancellationPolicy, setCancellationPolicy] = useState('');

  useEffect(() => {
    fetchWidgetConfig();
  }, []);

  const fetchWidgetConfig = async () => {
    try {
      const response = await api.get('/restaurants/widget-config');
      if (response.data) {
        setCustomOccasions(response.data.custom_occasions || []);
        setEnabledDefaults(response.data.enabled_default_occasions || DEFAULT_OCCASIONS.map(o => o.id));
        setCancellationPolicy(response.data.cancellation_policy || '');
      }
    } catch (err) {
      console.error('Error fetching config:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.patch('/restaurants/widget-config', {
        custom_occasions: customOccasions,
        enabled_default_occasions: enabledDefaults,
        cancellation_policy: cancellationPolicy
      });
      toast.success('Configuración guardada');
    } catch (err) {
      toast.error('Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const toggleDefaultOccasion = (id) => {
    if (enabledDefaults.includes(id)) {
      setEnabledDefaults(enabledDefaults.filter(o => o !== id));
    } else {
      setEnabledDefaults([...enabledDefaults, id]);
    }
  };

  const addOccasion = () => {
    if (!newOccasion.label.trim()) {
      toast.error('Ingresa un nombre para la ocasión');
      return;
    }
    setCustomOccasions([...customOccasions, { 
      id: `custom_${Date.now()}`, 
      label: newOccasion.label, 
      icon: newOccasion.icon 
    }]);
    setNewOccasion({ label: '', icon: 'Star' });
  };

  const removeOccasion = (id) => {
    setCustomOccasions(customOccasions.filter(o => o.id !== id));
  };

  const updateOccasionIcon = (id, icon) => {
    setCustomOccasions(customOccasions.map(o => o.id === id ? { ...o, icon } : o));
    setShowIconPicker(null);
  };

  const getIconComponent = (iconId) => {
    const found = ICON_LIBRARY.find(i => i.id === iconId);
    return found ? found.icon : Star;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <TopBar title="Configuración de Widget" />
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <TopBar title="Configuración de Widget de Reservas" />
      
      <div className="max-w-4xl mx-auto p-6 space-y-6">
        {/* Ocasiones Predefinidas */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
            <PartyPopper className="w-5 h-5 text-orange-500" />
            Ocasiones Predefinidas
          </h2>
          <p className="text-gray-500 text-sm mb-4">
            Selecciona las ocasiones que deseas mostrar en tu widget de reservaciones.
          </p>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {DEFAULT_OCCASIONS.map((occasion) => {
              const IconComponent = getIconComponent(occasion.icon);
              const isEnabled = enabledDefaults.includes(occasion.id);
              return (
                <button
                  key={occasion.id}
                  onClick={() => toggleDefaultOccasion(occasion.id)}
                  className={`flex items-center gap-3 p-4 rounded-xl border-2 transition-all ${
                    isEnabled 
                      ? 'border-orange-500 bg-orange-50' 
                      : 'border-gray-200 bg-gray-50 opacity-60 hover:opacity-80'
                  }`}
                >
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                    isEnabled ? 'bg-orange-100' : 'bg-gray-200'
                  }`}>
                    <IconComponent className={`w-5 h-5 ${isEnabled ? 'text-orange-500' : 'text-gray-500'}`} />
                  </div>
                  <span className={`font-medium ${isEnabled ? 'text-gray-900' : 'text-gray-500'}`}>
                    {occasion.label}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Ocasiones Personalizadas */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-orange-500" />
            Ocasiones Personalizadas
          </h2>
          <p className="text-gray-500 text-sm mb-4">
            Agrega ocasiones especiales adicionales que tus clientes pueden seleccionar.
          </p>

          {/* Lista de ocasiones */}
          <div className="space-y-3 mb-4">
            {customOccasions.map((occasion) => {
              const IconComponent = getIconComponent(occasion.icon);
              return (
                <div key={occasion.id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                  <div className="relative">
                    <button
                      onClick={() => setShowIconPicker(showIconPicker === occasion.id ? null : occasion.id)}
                      className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center hover:bg-orange-200 transition-colors"
                    >
                      <IconComponent className="w-5 h-5 text-orange-500" />
                    </button>
                    
                    {/* Icon Picker */}
                    {showIconPicker === occasion.id && (
                      <div className="absolute top-12 left-0 z-10 bg-white rounded-xl shadow-lg border p-3 grid grid-cols-4 gap-2">
                        {ICON_LIBRARY.map(({ id, icon: Icon, label }) => (
                          <button
                            key={id}
                            onClick={() => updateOccasionIcon(occasion.id, id)}
                            className={`w-10 h-10 rounded-lg flex items-center justify-center hover:bg-orange-100 transition-colors ${
                              occasion.icon === id ? 'bg-orange-200' : 'bg-gray-50'
                            }`}
                            title={label}
                          >
                            <Icon className="w-5 h-5 text-gray-700" />
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  <span className="flex-1 font-medium text-gray-700">{occasion.label}</span>
                  <button
                    onClick={() => removeOccasion(occasion.id)}
                    className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              );
            })}
            
            {customOccasions.length === 0 && (
              <p className="text-gray-400 text-sm text-center py-4">
                No hay ocasiones personalizadas. Agrega una abajo.
              </p>
            )}
          </div>

          {/* Agregar nueva ocasión */}
          <div className="flex gap-3">
            <div className="relative">
              <button
                onClick={() => setShowIconPicker(showIconPicker === 'new' ? null : 'new')}
                className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center hover:bg-gray-200 transition-colors"
              >
                {(() => {
                  const Icon = getIconComponent(newOccasion.icon);
                  return <Icon className="w-5 h-5 text-gray-600" />;
                })()}
              </button>
              
              {showIconPicker === 'new' && (
                <div className="absolute top-12 left-0 z-10 bg-white rounded-xl shadow-lg border p-3 grid grid-cols-4 gap-2">
                  {ICON_LIBRARY.map(({ id, icon: Icon, label }) => (
                    <button
                      key={id}
                      onClick={() => {
                        setNewOccasion({ ...newOccasion, icon: id });
                        setShowIconPicker(null);
                      }}
                      className={`w-10 h-10 rounded-lg flex items-center justify-center hover:bg-orange-100 transition-colors ${
                        newOccasion.icon === id ? 'bg-orange-200' : 'bg-gray-50'
                      }`}
                      title={label}
                    >
                      <Icon className="w-5 h-5 text-gray-700" />
                    </button>
                  ))}
                </div>
              )}
            </div>
            <input
              type="text"
              placeholder="Nombre de la ocasión"
              value={newOccasion.label}
              onChange={(e) => setNewOccasion({ ...newOccasion, label: e.target.value })}
              className="flex-1 px-4 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500"
              onKeyDown={(e) => e.key === 'Enter' && addOccasion()}
            />
            <button
              onClick={addOccasion}
              className="px-4 py-2 bg-orange-500 text-white rounded-xl font-medium hover:bg-orange-600 transition-colors flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Agregar
            </button>
          </div>
        </div>

        {/* Política de Cancelación */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
            <Edit2 className="w-5 h-5 text-orange-500" />
            Política de Cancelación
          </h2>
          <p className="text-gray-500 text-sm mb-4">
            Resumen que se mostrará en el widget (máximo 70 caracteres).
          </p>
          
          <div className="relative">
            <textarea
              value={cancellationPolicy}
              onChange={(e) => setCancellationPolicy(e.target.value.slice(0, 70))}
              placeholder="Ej: Cancelaciones con 24h de anticipación sin cargo."
              rows={2}
              className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 resize-none"
            />
            <span className={`absolute bottom-2 right-3 text-xs ${
              cancellationPolicy.length >= 70 ? 'text-red-500' : 'text-gray-400'
            }`}>
              {cancellationPolicy.length}/70
            </span>
          </div>
        </div>

        {/* Botón Guardar */}
        <button
          onClick={handleSave}
          disabled={saving}
          className="w-full py-4 bg-orange-500 text-white rounded-xl font-bold hover:bg-orange-600 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
        >
          {saving ? (
            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
          ) : (
            <Save className="w-5 h-5" />
          )}
          Guardar Configuración
        </button>
      </div>
    </div>
  );
}
